import { useState, useEffect } from 'react';

const THEME_KEY = 'csb2025_theme';
const THEME_MODE_KEY = 'csb2025_theme_mode'; // 'manual' or 'auto'

export const useTheme = (sunriseTime, sunsetTime) => {
  const [theme, setTheme] = useState('dark');
  const [themeMode, setThemeMode] = useState('manual'); // 'manual' or 'auto'

  // Determine if it's currently daytime based on sunrise/sunset
  const isDaytime = () => {
    if (!sunriseTime || !sunsetTime) return false;
    
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const sunrise = new Date(sunriseTime);
    const sunset = new Date(sunsetTime);
    
    const sunriseMinutes = sunrise.getHours() * 60 + sunrise.getMinutes();
    const sunsetMinutes = sunset.getHours() * 60 + sunset.getMinutes();
    
    return currentTime >= sunriseMinutes && currentTime < sunsetMinutes;
  };

  useEffect(() => {
    // Check localStorage on mount
    const savedTheme = localStorage.getItem(THEME_KEY);
    const savedMode = localStorage.getItem(THEME_MODE_KEY) || 'manual';
    
    setThemeMode(savedMode);
    
    if (savedMode === 'auto' && sunriseTime && sunsetTime) {
      // Set theme based on time of day
      setTheme(isDaytime() ? 'light' : 'dark');
    } else if (savedTheme === 'light' || savedTheme === 'dark') {
      setTheme(savedTheme);
    } else {
      // Default to dark mode for first-time visitors
      setTheme('dark');
      localStorage.setItem(THEME_KEY, 'dark');
    }
  }, [sunriseTime, sunsetTime]);

  // Update theme when in auto mode and time changes
  useEffect(() => {
    if (themeMode === 'auto' && sunriseTime && sunsetTime) {
      const checkAndUpdateTheme = () => {
        const newTheme = isDaytime() ? 'light' : 'dark';
        if (newTheme !== theme) {
          setTheme(newTheme);
        }
      };

      // Check immediately
      checkAndUpdateTheme();

      // Check every minute
      const interval = setInterval(checkAndUpdateTheme, 60000);
      return () => clearInterval(interval);
    }
  }, [themeMode, sunriseTime, sunsetTime, theme]);

  useEffect(() => {
    // Apply theme to document
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    setThemeMode('manual');
    localStorage.setItem(THEME_KEY, newTheme);
    localStorage.setItem(THEME_MODE_KEY, 'manual');
  };

  const enableAutoTheme = () => {
    setThemeMode('auto');
    localStorage.setItem(THEME_MODE_KEY, 'auto');
    
    // Immediately update theme based on time
    if (sunriseTime && sunsetTime) {
      const newTheme = isDaytime() ? 'light' : 'dark';
      setTheme(newTheme);
      localStorage.setItem(THEME_KEY, newTheme);
    }
  };

  const disableAutoTheme = () => {
    setThemeMode('manual');
    localStorage.setItem(THEME_MODE_KEY, 'manual');
  };

  return { theme, toggleTheme, themeMode, enableAutoTheme, disableAutoTheme };
};

export default useTheme;